import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TOPICS } from '../config/topics';
import { useTheme } from '../contexts/ThemeContext';
import LucideIcon from './LucideIcon';
import XPToast from './gamification/XPToast';


interface Challenge {
  id: string;
  title: string;
  description: string;
  topic: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  scenario: string;
  objective: string;
  constraints: string[];
  hints: string[];
  points: number;
  estimatedTime: string;
}

interface DifficultyProgress {
  [key: string]: number;
  beginner: number;
  intermediate: number;
  advanced: number;
  expert: number;
}

interface UserProgress {
  completedChallenges: number;
  totalScore: number;
  currentLevel: string;
  averageScore: number;
  topicProgress: {
    [topicId: string]: {
      completed: number;
      total: number;
      completedChallenges: string[];
      difficultyProgress?: DifficultyProgress;
    };
  };
}



// Sourced from official documentation (Anthropic's prompt-engineering tutorial, Google's
// Prompt Engineering whitepaper, DAIR.AI, OpenAI's cookbook, Learn Prompting) -- see
// docs/build-plan/07-curriculum-content-plan.md, Section 2, for the full citation trail.
// Sequence follows that plan's Tier-1 core-technique ordering (zero-shot first, ReAct
// deliberately deferred to the Frameworks track since it's the bridge into agentic content).
const BASIC_CHALLENGES = [
  {
    id: 'basic-1',
    title: 'Zero-Shot Prompting',
    difficulty: 'beginner' as const,
    scenario: 'You need an AI to classify customer support tickets by urgency, with no examples to show it -- just clear task instructions.',
    objective: 'Write a prompt that gets a correct, well-formatted classification using instructions alone -- no worked examples, no reasoning scaffolding. This is the baseline every other technique in this track improves on.',
    constraints: ['No examples included', 'State the exact task and valid output values explicitly', 'Rely only on instruction clarity, not demonstrations'],
    hints: ['State exactly which categories are valid, e.g. "Urgent / Normal / Low"', 'Tell the model exactly what to return -- a label, not an explanation', 'Notice where instructions alone still leave the AI guessing -- that gap is what the next lessons address'],
    points: 100,
    estimatedTime: '10 minutes'
  },
  {
    id: 'basic-2',
    title: 'Clear, Direct Instructions',
    difficulty: 'beginner' as const,
    scenario: 'You need to ask an AI to write a professional email, but a vague request keeps producing generic, unusable drafts.',
    objective: "Write a prompt specific enough that a colleague reading it cold would know exactly what output to expect -- if a request would confuse a colleague, it will confuse the model too.",
    constraints: ['Specify the exact tone (e.g. formal, warm-but-firm)', "State the email's purpose and desired outcome explicitly", 'Define the audience and any length limit'],
    hints: ['Vague verbs like "improve" or "help with" are a warning sign -- replace them with a concrete action', 'Say what NOT to include, not just what to include', 'Read your own prompt back cold: would it confuse someone with no other context?'],
    points: 100,
    estimatedTime: '10 minutes'
  },
  {
    id: 'basic-3',
    title: 'Role & System Prompting',
    difficulty: 'intermediate' as const,
    scenario: "You want an AI to act as an experienced financial advisor answering a client's retirement question -- not just answer generically.",
    objective: 'Write a prompt that assigns the AI a specific persona and expertise level, and shape how that framing changes both tone and the substance of the answer.',
    constraints: ['Open with an explicit "You are..." role assignment', "State the persona's expertise level and who they're addressing", 'Set at least one explicit boundary on what the persona should or should not do'],
    hints: ['Role prompting can improve more than tone -- it can improve reasoning accuracy on tasks with implicit expertise', 'Give the persona a boundary, not just a title, e.g. "as a fee-only advisor, you do not recommend specific investment products"', 'A role assignment works whether it sits in a system prompt or the user turn -- try both'],
    points: 150,
    estimatedTime: '15 minutes'
  },
  {
    id: 'basic-4',
    title: 'Structured Output & Data Separation',
    difficulty: 'intermediate' as const,
    scenario: "You need a structured analysis with clearly separated sections, and the text you're summarizing contains instruction-like phrasing that could confuse the model about where your instructions end and its input data begins.",
    objective: "Write a prompt that specifies an exact output structure (tags or a schema) and clearly delimits any input data from your instructions, so the model can't misread one as the other.",
    constraints: ['Specify the output structure explicitly (e.g. XML tags or a defined schema)', "Wrap any variable/input content in an explicit delimiter, not left bare", "Name every required section or field -- don't leave the structure implicit"],
    hints: ['XML tags like <analysis> are a strong default for structuring output', "If you're substituting in a block of user-provided text, wrap it -- e.g. <document>...</document> -- so it can't be mistaken for an instruction", 'Ask yourself: could this prompt be misread if the input data itself contained an instruction-like sentence?'],
    points: 150,
    estimatedTime: '15 minutes'
  },
  {
    id: 'basic-5',
    title: 'Few-Shot Prompting',
    difficulty: 'advanced' as const,
    scenario: "You want the AI to classify short product reviews using your team's specific rubric -- one that isn't inferable from the category names alone.",
    objective: 'Write a prompt with 3-5 worked examples that teach the AI your classification pattern through demonstration, not description.',
    constraints: ['Include 3-5 examples, each a clear input-output pair', 'Balance and shuffle your examples across classes rather than grouping them by label', "Keep the AI's actual task item clearly separated from the example block"],
    hints: ['Models can latch onto the order or grouping of examples, not just their content -- mix up the classes', 'Consistency of format matters more than raw example count', 'Reach for few-shot when a rule is easier to show than to describe'],
    points: 200,
    estimatedTime: '15 minutes'
  },
  {
    id: 'basic-6',
    title: 'Chain-of-Thought Prompting',
    difficulty: 'advanced' as const,
    scenario: 'You need the AI to solve a multi-step word problem, and a direct-answer prompt keeps producing confidently wrong numbers.',
    objective: 'Write a prompt that gets the AI to reason step by step before giving its final answer -- either with worked reasoning examples, or the zero-shot "let\'s think step by step" cue.',
    constraints: ['Explicitly request step-by-step reasoning before the final answer', 'The reasoning must appear in the visible output -- there is no hidden reasoning channel to rely on', 'Make the final answer clearly identifiable and separate from the reasoning trace'],
    hints: ['Try the zero-shot version first -- literally appending "let\'s think step by step" often works with no examples at all', 'A model told to "think it through" while outputting only a final number has not actually reasoned -- the steps must be written out', 'For a harder problem, show 1-2 worked examples of the reasoning pattern you want'],
    points: 250,
    estimatedTime: '20 minutes'
  },
  {
    id: 'basic-7',
    title: 'Self-Consistency',
    difficulty: 'expert' as const,
    scenario: 'A single chain-of-thought attempt at a tricky logic puzzle sometimes gets it right, sometimes wrong -- you need a more reliable answer without a bigger model.',
    objective: 'Design a self-consistency approach: generate several independent chain-of-thought attempts at the same problem, then take the majority-vote answer instead of trusting any one attempt.',
    constraints: ['Reuse the same CoT-style prompt for every attempt -- vary only the sampling, not the instructions', 'Explicitly extract one final answer from each independent attempt', "State the majority-vote rule you'll use to pick the consensus answer"],
    hints: ['This only works with real sampling diversity -- running one deterministic prompt once and calling it "self-consistency" defeats the point', 'Self-consistency trades cost for reliability -- three to five attempts is a common range, not fifty', 'Reserve this for problems with a discrete, checkable answer, not open-ended writing'],
    points: 300,
    estimatedTime: '20 minutes'
  },
  {
    id: 'basic-8',
    title: 'Prompt Chaining & Task Decomposition',
    difficulty: 'expert' as const,
    scenario: 'You need to turn a rough idea into a polished blog outline, and asking for the whole thing in one shot produces shallow, generic results.',
    objective: "Design a sequence of at least three connected prompts, where each step's output becomes the next step's input, instead of attempting the whole task in a single prompt.",
    constraints: ['Break the task into at least 3 sequential sub-prompts', "Each later prompt must explicitly consume the previous step's output, not restate it from scratch", 'State what each individual step is responsible for producing'],
    hints: ["If one prompt is trying to do research, structure, and polish all at once, that's usually 3 prompts, not 1", 'A "step-back" prompt -- asking a general question before the specific one -- is one useful chaining pattern', "Chaining trades one big, unreliable prompt for several small, checkable ones -- treat each step's output as something you could inspect"],
    points: 350,
    estimatedTime: '25 minutes'
  },
  {
    id: 'basic-9',
    title: 'Zero-Shot Chain-of-Thought',
    difficulty: 'advanced' as const,
    scenario: "You need a quick, correct answer to a reasoning problem with a few logical steps -- like whether a discount stacking rule applies, or whether a shift swap violates a rest-period rule -- and you don't have time to write worked examples, but a direct-answer prompt keeps getting the arithmetic or logic wrong.",
    objective: 'Write a prompt that gets a reliable, correct answer to a multi-step reasoning problem using only a step-by-step instruction -- no worked examples, no demonstration of the reasoning pattern.',
    constraints: ['No worked examples of the reasoning pattern included', 'Explicitly requests step-by-step reasoning before the final answer', 'States every fact the reasoning depends on, so nothing is left for the model to assume'],
    hints: ['"Let\'s think step by step" (or an equivalent instruction) is the whole technique -- put it before you ask for the answer, not after', "Give the model every fact the reasoning depends on explicitly -- it can't reason correctly about a date or rule you left implied", "This differs from chain-of-thought by having zero worked examples -- if you catch yourself writing one, you've switched techniques"],
    points: 250,
    estimatedTime: '20 minutes'
  },
  {
    id: 'basic-10',
    title: 'ReAct (Reason + Act)',
    difficulty: 'expert' as const,
    scenario: 'You need an assistant to answer a question that requires looking something up and then reasoning about what comes back -- like checking a policy limit against a looked-up rate, or checking whether a scheduled event falls inside a deadline -- and a direct-answer prompt just guesses at the looked-up fact instead of treating it as something to actually retrieve first.',
    objective: 'Write a prompt that sets up a Thought, Action, Observation loop: name the available tools/actions explicitly, and instruct the model to reason, take one action, and wait for its result before reasoning again -- rather than answering in one uninterrupted guess.',
    constraints: ['Names at least one available action/tool explicitly, in a callable-looking form', 'Explicitly instructs the model to alternate Thought, Action, and Observation rather than answering directly', 'Requires reasoning to resume after an observation, not stop after the first action'],
    hints: ['Name each available action like a function signature -- "search(query)" reads as something to call, "look things up" does not', 'The instruction has to forbid answering before acting -- otherwise the model will happily skip straight to a plausible-sounding guess', 'A real ReAct loop reasons again after the observation comes back, not just once at the start -- your prompt should ask for that second Thought explicitly'],
    points: 400,
    estimatedTime: '30 minutes'
  },
  {
    id: 'basic-11',
    title: 'Tree of Thoughts (Optional)',
    difficulty: 'expert' as const,
    scenario: "You're facing a decision where the first reasonable-sounding approach might not be the best one -- like choosing an architecture, a pricing structure, or a process fix -- and a single-chain prompt commits to whatever idea comes first instead of comparing real alternatives.",
    objective: 'Write a prompt that generates multiple genuinely different approaches to the same problem, evaluates each one on stated criteria, and only then compares and recommends -- rather than developing one idea straight through to an answer.',
    constraints: ['Requests at least 3 genuinely distinct approaches, not restatements of one idea', 'Evaluates each approach against the same stated criteria before comparing', 'Requires the final recommendation to explain what was rejected and why'],
    hints: ['Explicitly forbid "variations on one idea" -- without that guard, a model asked for three approaches will often just restate the same idea three ways', 'Give every approach the same evaluation criteria, so the comparison at the end is actually apples-to-apples', "Ask the model to say what it rejected and why -- that's what proves the other branches were real, not decorative"],
    points: 450,
    estimatedTime: '30 minutes'
  },
  {
    id: 'basic-12',
    title: 'Automatic Prompt Engineering (Optional)',
    difficulty: 'expert' as const,
    scenario: "You have a task you need a good prompt for, but you're not sure which wording will actually work best, and you're too close to your own draft to judge it objectively -- like a data-extraction prompt that needs to handle several messy real-world variations.",
    objective: 'Write a meta-prompt that gets the model to generate several genuinely different candidate prompts for a target task, score each on stated criteria, and refine the best one -- rather than hand-tuning a single draft by guesswork.',
    constraints: ['Requests multiple candidate prompts using genuinely different approaches for the same target task', 'Scores or evaluates the candidates against named, specific criteria', 'Ends by selecting and refining the best candidate, not just ranking them'],
    hints: ['Ask for candidates that use genuinely different approaches, not three drafts that are really the same idea reworded', 'Name specific scoring criteria up front -- "which is better" is not checkable, "does it handle an empty case" is', "Close the loop: the technique isn't finished at scoring, it ends with refining the winner's weakest dimension"],
    points: 500,
    estimatedTime: '35 minutes'
  },
  {
    id: 'basic-13',
    title: 'Sampling parameters: temperature, top_p, and top_k',
    difficulty: 'intermediate' as const,
    scenario: "Your team is building three different features that all call the same model: (1) a support ticket classifier that must pick one of five fixed categories, (2) a creative-writing assistant that drafts short story openings, and (3) a meeting-summary tool that should produce a consistent, professional-sounding summary of the same transcript every time it's re-run.",
    objective: 'Write three separate API calls (one per feature), each with a temperature value you have deliberately chosen and explicitly justified in a comment, based on whether that feature needs consistency or creative variety.',
    constraints: ['Includes three separate API calls, each with an explicit temperature value', 'The ticket classifier and meeting-summary calls use a low temperature (close to 0), and the creative-writing call uses a meaningfully higher one', 'Each temperature choice is justified in a comment explaining why that task needs consistency or variety'],
    hints: ['temperature ranges from 0.0 (most focused/consistent) to 1.0 (most random/creative) -- there is no universally correct value, it depends on whether the task has one right answer or many acceptable ones.', 'The ticket classifier and meeting summarizer both want consistency (low temperature); the story-opening drafter wants variety (higher temperature).', 'State your reasoning in a comment next to each temperature value -- a value with no justification does not demonstrate understanding of the tradeoff.'],
    points: 180,
    estimatedTime: '20 minutes'
  },
  {
    id: 'basic-14',
    title: 'Context window and conversation history management',
    difficulty: 'intermediate' as const,
    scenario: "You're building a customer-support chat assistant that can run for dozens of turns in a single session. Left unmanaged, the resent history eventually approaches the model's context window, increasing cost and risking a request that's too large -- but naively deleting old turns can also lose context the user actually still needs (like their original issue description).",
    objective: 'Write a function that trims a growing messages list before each API call -- keeping the first message (the original issue description) plus only the N most recent turns -- and show it being used in a loop that keeps the conversation running without ever sending the full untrimmed history once a turn threshold is passed.',
    constraints: ['Defines a function that trims a messages list, keeping the first message plus only the most recent N messages once a threshold is exceeded', 'Calls this trimming function before each API request in a loop, not just once', 'The API call itself uses the trimmed list, and the full untrimmed list is never sent once the threshold is passed'],
    hints: ['The API has no memory between calls -- your own code is responsible for both building the history and deciding when and how to trim it.', "Keep the first message specifically, since it usually carries the original context (e.g. the user's initial issue) that later turns depend on -- dropping it loses information a pure most-recent-N-turns trim would lose.", 'Trimming should happen before constructing the request, not after -- the whole point is to keep what gets sent under a size threshold.'],
    points: 190,
    estimatedTime: '20 minutes'
  }
];

const DIFFICULTIES = [
  { level: 'beginner', name: 'Beginner', description: 'Basic concepts and simple scenarios', points: 100, unlocked: true, requiredChallenges: 0, challengesPerLevel: 2 },
  { level: 'intermediate', name: 'Intermediate', description: 'Multi-step analysis and reasoning', points: 200, unlocked: true, requiredChallenges: 2, challengesPerLevel: 2 },
  { level: 'advanced', name: 'Advanced', description: 'Complex, multi-constraint scenarios', points: 300, unlocked: false, requiredChallenges: 4, challengesPerLevel: 2 },
  { level: 'expert', name: 'Expert', description: 'Ambiguous requirements and edge cases', points: 500, unlocked: false, requiredChallenges: 6, challengesPerLevel: 2 }
];

const Challenges: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userProgress, setUserProgress] = useState<UserProgress>({
    completedChallenges: 0,
    totalScore: 0,
    currentLevel: 'Beginner',
    averageScore: 0,
    topicProgress: {
      'basic-prompt-engineering': { completed: 0, total: 14, completedChallenges: [] },
      'writing-editing': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'summarizing-research': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'data-analysis': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'coding-debugging': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'tutoring-explaining': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'customer-communication': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'planning-decisions': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'creative-ideation': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
      'agent-design': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } }
    }
  });

  const loadUserProgress = useCallback(async () => {
    try {
      const response = await API.get('/progress');
      if (response && response.length > 0) {
        const progressData = response[0];
        
        // Calculate average score from scores array
        const scores = progressData.scores || [];
        const averageScore = scores.length > 0 
          ? Math.round(scores.reduce((sum: number, s: number) => sum + s, 0) / scores.length)
          : 0;
        
        const defaultTopicProgress = {
          'basic-prompt-engineering': { completed: 0, total: 14, completedChallenges: [] },
          'writing-editing': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'summarizing-research': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'data-analysis': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'coding-debugging': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'tutoring-explaining': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'customer-communication': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'planning-decisions': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'creative-ideation': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } },
          'agent-design': { completed: 0, total: 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } }
        };
        
        setUserProgress({
          completedChallenges: progressData.challengesCompleted || 0,
          totalScore: progressData.points || 0,
          currentLevel: `Level ${progressData.level || 1}`,
          averageScore: averageScore,
          topicProgress: progressData.topicProgress || defaultTopicProgress
        });
        
        // Sync completed challenges from progress
        const basicProgress = progressData.topicProgress?.['basic-prompt-engineering'];
        if (basicProgress?.completedChallenges) {
          const completedIndices = BASIC_CHALLENGES
            .map((c, idx) => basicProgress.completedChallenges.includes(c.id) ? idx : -1)
            .filter(idx => idx !== -1);
          setCompletedBasicChallenges(completedIndices);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading user progress:', error);
      }
    }
  }, []);
  
  useEffect(() => {
    loadUserProgress();
    loadAvailableChallenges();
    
    // Auto-select topic from URL parameter
    const topicParam = searchParams.get('topic');
    if (topicParam && TOPICS.find(t => t.id === topicParam)) {
      setSelectedTopic(topicParam);
    }
    
    const handleChallengeCompleted = (event: CustomEvent) => {
      setTimeout(() => {
        loadUserProgress();
      }, 1000);
    };
    
    window.addEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    
    return () => {
      window.removeEventListener('challengeCompleted', handleChallengeCompleted as EventListener);
    };
  }, [searchParams]);

  const loadAvailableChallenges = async () => {
    try {
      const response = await API.get('/challenges/recommended');
      if (response && response.length > 0) {
        setAvailableChallenges(response);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading challenges:', error);
      }
    }
  };
  const [completedBasicChallenges, setCompletedBasicChallenges] = useState<number[]>([]);
  const [xpToastPoints, setXpToastPoints] = useState<number | null>(null);
  const [, setAvailableChallenges] = useState<any[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>(() => {
    return localStorage.getItem('selectedTopic') || 'basic-prompt-engineering';
  });
  const [selectedBasicChallenge, setSelectedBasicChallenge] = useState<number>(0);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>(() => {
    return localStorage.getItem('selectedDifficulty') || 'beginner';
  });
  const [currentChallenge, setCurrentChallenge] = useState<Challenge | null>(null);
  const [userPrompt, setUserPrompt] = useState<string>('');
  const [aiResponse, setAiResponse] = useState<string>('');
  const [evaluation, setEvaluation] = useState<any>(null);
  const [previewEvaluation, setPreviewEvaluation] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingNextChallenge, setLoadingNextChallenge] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'selection' | 'active' | 'completed'>('selection');

  const generateChallenge = async (challengeIndexOrEvent?: number | React.MouseEvent) => {
    if (!selectedTopic) return;

    // Every lesson in this track now has study/complete/produce content, so the
    // track opens on the worked example instead of a blank textarea. The old
    // blank-page flow below is still what the produce rung ends in.
    // See docs/build-plan/08-lesson-design.md.
    if (selectedTopic === 'basic-prompt-engineering') {
      const index = typeof challengeIndexOrEvent === 'number' ? challengeIndexOrEvent : selectedBasicChallenge;
      navigate(`/lessons/${BASIC_CHALLENGES[index].id}`);
      return;
    }

    setLoading(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Generating challenge for topic:', selectedTopic, 'difficulty:', selectedDifficulty);
      }

      const response = await API.post('/challenges/generate', {
        topic: selectedTopic,
        difficulty: selectedDifficulty
      });

      setCurrentChallenge(response);
      setUserPrompt('');
      setAiResponse('');
      setEvaluation(null);
      setViewMode('active');
    } catch (error: any) {
      console.error('Error generating challenge:', error);
      
      // More specific error messages
      let errorMessage = 'Unknown error occurred';
      if (error.message) {
        if (error.message.includes('timed out') || error.message.includes('timeout')) {
          errorMessage = 'The server is taking longer than usual to generate your challenge. This sometimes happens when the AI is busy. Please try again in a moment.';
        } else if (error.message.includes('Session expired')) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network connection error. Please check your internet connection.';
        } else {
          errorMessage = error.message;
        }
      }
      
      alert(`Error generating challenge: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const testPrompt = async () => {
    if (!userPrompt.trim() || !currentChallenge) return;
    
    setLoading(true);
    try {
      const response = await API.post('/prompt/test', {
        challengeId: currentChallenge.id,
        userPrompt,
        challengeObjective: currentChallenge.objective,
        challengeConstraints: currentChallenge.constraints,
        challengeScenario: currentChallenge.scenario
      });
      setPreviewEvaluation(response.evaluation);
    } catch (error) {
      setAiResponse('Error: Failed to get AI response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalProgress = async (challengeData: any) => {
    try {
      await API.post('/progress/sync', {
        challengeId: challengeData.challengeId,
        topic: challengeData.topic,
        points: challengeData.points,
        difficulty: challengeData.difficulty,
        score: challengeData.score,
        timestamp: new Date().toISOString()
      });
      
      window.dispatchEvent(new CustomEvent('challengeCompleted', {
        detail: challengeData
      }));
    } catch (error) {
      // Error syncing progress
    }
  };

  const submitChallenge = async () => {
    if (!currentChallenge || !userPrompt) return;
    
    setLoading(true);
    try {
      const response = await API.post('/challenges/submit', {
        challengeId: currentChallenge.id,
        userPrompt,
        aiResponse: aiResponse || undefined,
        challengeObjective: currentChallenge.objective,
        challengeConstraints: currentChallenge.constraints,
        challengeScenario: currentChallenge.scenario,
        temperature: 0.7,
        top_k: 40,
        top_p: 0.9,
        max_tokens: 500
      });
      
      if (response.aiResponse && !aiResponse) {
        setAiResponse(response.aiResponse);
      }
      
      setEvaluation(response);
      setViewMode('completed');
      if (response.passed) {
        const currentTopicProgress = userProgress.topicProgress[selectedTopic] || { completed: 0, total: selectedTopic === 'basic-prompt-engineering' ? 12 : 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } };
        
        // Check if this is a new completion
        let shouldAwardPoints = false;
        
        if (selectedTopic === 'basic-prompt-engineering') {
          // For Basic PE: Only award points if challenge not already completed
          shouldAwardPoints = !currentTopicProgress.completedChallenges.includes(currentChallenge.id);
        } else {
          // For applied-practice topics: only award points if difficulty level has less than 2 completions
          const currentDifficultyProgress = currentTopicProgress?.difficultyProgress || { beginner: 0, intermediate: 0, advanced: 0, expert: 0 };
          shouldAwardPoints = (currentDifficultyProgress[currentChallenge.difficulty] || 0) < 2;
        }

        if (shouldAwardPoints) {
          setXpToastPoints(currentChallenge.points);
          const challengeData = {
            challengeId: currentChallenge.id,
            topic: selectedTopic,
            points: currentChallenge.points,
            difficulty: currentChallenge.difficulty,
            score: response.score
          };
          
          setUserProgress(prev => {
            const currentTopicProgress = prev.topicProgress[selectedTopic] || { completed: 0, total: selectedTopic === 'basic-prompt-engineering' ? 12 : 8, completedChallenges: [], difficultyProgress: { beginner: 0, intermediate: 0, advanced: 0, expert: 0 } };
            const currentDifficultyProgress: DifficultyProgress = currentTopicProgress?.difficultyProgress || {
              beginner: 0,
              intermediate: 0,
              advanced: 0,
              expert: 0
            };
            
            const isNewChallenge = !currentTopicProgress.completedChallenges.includes(currentChallenge.id);
            const updatedDifficultyProgress: DifficultyProgress = {
              ...currentDifficultyProgress,
              [currentChallenge.difficulty]: (currentDifficultyProgress[currentChallenge.difficulty] || 0) + 1
            };
            
            return {
              ...prev,
              completedChallenges: prev.completedChallenges + 1,
              totalScore: prev.totalScore + currentChallenge.points,
              topicProgress: {
                ...prev.topicProgress,
                [selectedTopic]: {
                  ...currentTopicProgress,
                  completed: currentTopicProgress.completed + 1,
                  completedChallenges: isNewChallenge ? [...currentTopicProgress.completedChallenges, currentChallenge.id] : currentTopicProgress.completedChallenges,
                  difficultyProgress: updatedDifficultyProgress
                }
              }
            };
          });
          
          if (selectedTopic === 'basic-prompt-engineering') {
            setCompletedBasicChallenges(prev => {
              if (!prev.includes(selectedBasicChallenge)) {
                return [...prev, selectedBasicChallenge];
              }
              return prev;
            });
          }
          
          await updateGlobalProgress(challengeData);
        }
      }
    } catch (error) {
      const mockEval = {
        passed: false,
        score: 0,
        feedback: 'Unable to evaluate at this time. Please try again.',
        suggestions: ['Check your internet connection', 'Try submitting again'],
        breakdown: { 
          constraintAdherence: 0,
          constraintReasoning: 'Evaluation failed',
          scenarioRelevance: 0,
          scenarioReasoning: 'Evaluation failed',
          objectiveAlignment: 0,
          objectiveReasoning: 'Evaluation failed',
          techniqueApplication: 0,
          techniqueReasoning: 'Evaluation failed'
        }
      };
      setEvaluation(mockEval);
      setViewMode('completed');
    } finally {
      setLoading(false);
    }
  };

  const isChallengeUnlocked = (challengeIndex: number) => {
    if (challengeIndex === 0) return true;
    return completedBasicChallenges.includes(challengeIndex - 1);
  };

  const getChallengeStatus = (challengeIndex: number) => {
    if (completedBasicChallenges.includes(challengeIndex)) return 'completed';
    if (isChallengeUnlocked(challengeIndex)) return 'unlocked';
    return 'locked';
  };

  const getTopicIcon = (topicId: string) => {
    const topic = TOPICS.find(t => t.id === topicId);
    return topic?.icon || 'default';
  };

  const getDifficultyColor = (level: string) => {
    // Difficulty colors: Green (beginner), Blue (intermediate), Purple (advanced), Red (expert)
    const colors = {
      beginner: isDarkMode ? 'bg-green-900/40 text-green-300 border-green-700' : 'bg-green-100 text-green-700 border-green-300',
      intermediate: isDarkMode ? 'bg-blue-900/40 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-700 border-blue-300',
      advanced: isDarkMode ? 'bg-purple-900/40 text-purple-300 border-purple-700' : 'bg-purple-100 text-purple-700 border-purple-300',
      expert: isDarkMode ? 'bg-red-900/40 text-red-300 border-red-700' : 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[level as keyof typeof colors] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700');
  };

  const backToSelection = () => {
    setViewMode('selection');
    setCurrentChallenge(null);
    setUserPrompt('');
    setAiResponse('');
    setEvaluation(null);
    setPreviewEvaluation(null);
  };

  const getNextDifficulty = (): string => {
    const topicProgress = userProgress.topicProgress[selectedTopic];
    if (!topicProgress?.difficultyProgress) return 'beginner';
    
    const dp = topicProgress.difficultyProgress;
    
    // Check current difficulty first - if less than 2, stay on current
    if (dp[selectedDifficulty] < 2) {
      return selectedDifficulty;
    }
    
    // Otherwise, move to next difficulty level
    if (selectedDifficulty === 'beginner' && dp.beginner >= 2) return 'intermediate';
    if (selectedDifficulty === 'intermediate' && dp.intermediate >= 2) return 'advanced';
    if (selectedDifficulty === 'advanced' && dp.advanced >= 2) return 'expert';
    if (selectedDifficulty === 'expert' && dp.expert >= 2) return 'expert'; // Stay on expert
    
    return selectedDifficulty;
  };

  const nextChallenge = async () => {
    setLoadingNextChallenge(true);
    setPreviewEvaluation(null);
    try {
      if (selectedTopic === 'basic-prompt-engineering') {
        const nextIndex = selectedBasicChallenge + 1;
        if (nextIndex < BASIC_CHALLENGES.length) {
          setSelectedBasicChallenge(nextIndex);
          await generateChallenge(nextIndex);
        } else {
          backToSelection();
        }
      } else {
        // Determine next difficulty for dynamically-generated topics
        const nextDiff = getNextDifficulty();
        setSelectedDifficulty(nextDiff);
        localStorage.setItem('selectedDifficulty', nextDiff);
        await generateChallenge();
      }
    } finally {
      setLoadingNextChallenge(false);
    }
  };

  const tryAgain = () => {
    setUserPrompt('');
    setAiResponse('');
    setEvaluation(null);
    setPreviewEvaluation(null);
    setViewMode('active');
  };

  return (
    <div className="space-y-6">
      {xpToastPoints !== null && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 50 }}>
          <XPToast points={xpToastPoints} onDone={() => setXpToastPoints(null)} />
        </div>
      )}
      {/* Header */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
        <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
          Prompt Engineering Challenges
        </h1>
        <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>
          Master prompt engineering through structured missions and real-world scenarios
        </p>
        
        <div className="mt-4 flex items-center space-x-6">
          <div className={`${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} px-4 py-2 rounded-lg`}>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              Rank: {userProgress.currentLevel}
            </span>
          </div>
          <div className={`${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'} px-4 py-2 rounded-lg`}>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
              Missions: {userProgress.completedChallenges}
            </span>
          </div>
          <div className={`${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'} px-4 py-2 rounded-lg`}>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
              Credits: {userProgress.totalScore}
            </span>
          </div>
          <div className={`${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'} px-4 py-2 rounded-lg`}>
            <span className={`text-sm font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
              Avg Score: {userProgress.averageScore}/100
            </span>
          </div>
        </div>
      </div>

      {/* Topic and Difficulty Selection */}
      {viewMode === 'selection' && (
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
        <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Select Challenge Parameters</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Topics */}
          <div>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
              Challenge Topic
            </label>
            <div className="space-y-2">
              {TOPICS.map((topic) => (
                <div
                  key={topic.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedTopic === topic.id
                      ? isDarkMode ? 'border-blue-400 bg-blue-900/20' : 'border-blue-500 bg-blue-50'
                      : isDarkMode ? 'border-gray-600 hover:border-gray-500 bg-gray-700' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedTopic(topic.id);
                    localStorage.setItem('selectedTopic', topic.id);
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <LucideIcon name={topic.icon} size={24} />
                    <div>
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{topic.name}</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{topic.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Difficulties */}
          <div>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
              {selectedTopic === 'basic-prompt-engineering' ? 'Challenge Info' : 'Difficulty Level'}
            </label>
            {selectedTopic === 'basic-prompt-engineering' ? (
              <div className="space-y-3">
                {/* Progress Bar */}
                <div className={`p-3 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`flex justify-between text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                    <span>Topic Progress</span>
                    <span>{userProgress.topicProgress[selectedTopic]?.completed || 0}/{userProgress.topicProgress[selectedTopic]?.total || 8} completed</span>
                  </div>
                  <div className={`w-full rounded-full h-2 mb-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((userProgress.topicProgress[selectedTopic]?.completed || 0) / (userProgress.topicProgress[selectedTopic]?.total || 8)) * 100}%` }}
                    ></div>
                  </div>
                </div>
                
                {/* Challenge List */}
                <div className="space-y-2">
                  {BASIC_CHALLENGES.map((challenge, index) => {
                    const status = getChallengeStatus(index);
                    const isUnlocked = isChallengeUnlocked(index);
                    const isSelected = selectedBasicChallenge === index;
                    
                    return (
                      <div
                        key={challenge.id}
                        onClick={() => isUnlocked && setSelectedBasicChallenge(index)}
                        className={`p-3 border rounded-lg transition-colors cursor-pointer ${
                          isSelected && isUnlocked
                            ? isDarkMode ? 'border-blue-400 bg-blue-900/30' : 'border-blue-500 bg-blue-50'
                            : status === 'completed'
                            ? isDarkMode ? 'border-green-400 bg-green-900/30' : 'border-green-300 bg-green-50'
                            : status === 'locked'
                            ? isDarkMode ? 'border-gray-600 bg-gray-700 cursor-not-allowed opacity-60' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                            : isDarkMode ? 'border-gray-600 hover:border-gray-500 bg-gray-700/50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                              status === 'completed'
                                ? 'bg-green-500 text-white'
                                : status === 'locked'
                                ? isDarkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-300 text-gray-500'
                                : isSelected
                                ? 'bg-blue-500 text-white'
                                : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {status === 'completed' ? <LucideIcon name="status-check" size={14} /> : status === 'locked' ? <LucideIcon name="status-locked" size={14} /> : index + 1}
                            </div>
                            <div>
                              <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{challenge.title}</div>
                              <div className={`text-xs ${getDifficultyColor(challenge.difficulty)} px-2 py-0.5 rounded inline-block mt-1`}>
                                {challenge.difficulty}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              {challenge.points} pts
                            </div>
                            {!isUnlocked && index > 0 && (
                              <div className={`text-xs mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                Complete #{index} first
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`p-3 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                  <div className={`flex justify-between text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span>Topic Progress</span>
                    <span>{userProgress.topicProgress[selectedTopic]?.completed || 0}/{userProgress.topicProgress[selectedTopic]?.total || 8} completed</span>
                  </div>
                  <div className={`w-full rounded-full h-2 mb-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((userProgress.topicProgress[selectedTopic]?.completed || 0) / (userProgress.topicProgress[selectedTopic]?.total || 10)) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <div className="space-y-2">
                  {DIFFICULTIES.map((diff) => {
                    const topicDifficultyProgress = userProgress.topicProgress[selectedTopic]?.difficultyProgress?.[diff.level] || 0;
                    const totalChallengesInTopic = userProgress.topicProgress[selectedTopic]?.completed || 0;
                    const isUnlocked = totalChallengesInTopic >= diff.requiredChallenges;
                    const isCompleted = topicDifficultyProgress >= diff.challengesPerLevel;
                    
                    return (
                      <div
                        key={diff.level}
                        className={`p-3 border rounded-lg transition-colors ${
                          !isUnlocked
                            ? isDarkMode ? 'border-gray-600 bg-gray-700 cursor-not-allowed opacity-50' : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                            : isCompleted
                            ? isDarkMode ? 'border-green-400 bg-green-900/30 cursor-pointer' : 'border-green-300 bg-green-50 cursor-pointer'
                            : selectedDifficulty === diff.level
                            ? isDarkMode ? 'border-blue-400 bg-blue-900/30 cursor-pointer' : 'border-blue-500 bg-blue-50 cursor-pointer'
                            : isDarkMode ? 'border-gray-600 hover:border-gray-500 cursor-pointer bg-gray-700/50' : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => {
                          if (isUnlocked) {
                            setSelectedDifficulty(diff.level);
                            localStorage.setItem('selectedDifficulty', diff.level);
                          }
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{diff.name}</div>
                              {isCompleted && <LucideIcon name="status-check" size={16} className="text-green-500" />}
                            </div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{diff.description}</div>
                            {isUnlocked && (
                              <div className={`text-xs mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                Progress: {topicDifficultyProgress}/{diff.challengesPerLevel} challenges completed
                              </div>
                            )}
                            {!isUnlocked && (
                              <div className={`text-xs mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                                Complete {diff.requiredChallenges} challenges to unlock
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              {diff.points} pts
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={generateChallenge}
          disabled={!selectedTopic || loading || (selectedTopic === 'basic-prompt-engineering' && !isChallengeUnlocked(selectedBasicChallenge))}
          className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {selectedTopic === 'basic-prompt-engineering' ? 'Start lesson' : loading ? 'Generating Challenge... (this may take 30+ seconds)' : 'Generate Challenge'}
        </button>
      </div>
      )}

      {/* Current Challenge */}
      {currentChallenge && viewMode === 'active' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={backToSelection}
              className={`flex items-center ${isDarkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
            >
              <span className="mr-2">←</span> Back to Selection
            </button>
            <div className="flex items-center space-x-4">
              <span className="bg-gray-100 px-3 py-1 rounded-full text-sm inline-flex items-center gap-1">
                <LucideIcon name={getTopicIcon(currentChallenge.topic)} size={14} /> {TOPICS.find(t => t.id === currentChallenge.topic)?.name}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${getDifficultyColor(currentChallenge.difficulty)}`}>
                {currentChallenge.difficulty}
              </span>
              <span className="bg-green-100 px-3 py-1 rounded-full text-sm text-green-700">
                {currentChallenge.points} pts
              </span>
            </div>
          </div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>{currentChallenge.title}</h2>
        </div>
      )}

      {/* Challenge Instructions */}
      {currentChallenge && viewMode === 'active' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          {/* Grading Rubric */}
          <details className={`mb-4 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
            <summary className={`cursor-pointer font-semibold inline-flex items-center gap-1.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'} hover:text-blue-600`}>
              <LucideIcon name="graded-info" size={16} /> How is this graded? (Click to expand)
            </summary>
            <div className={`mt-3 space-y-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <p className="font-medium">Your submission will be evaluated on 4 criteria (25 points each):</p>
              <div className="space-y-3 ml-4">
                <div>
                  <span className="font-semibold text-green-600">1. Constraint Adherence (25 points)</span>
                  <p className="ml-4 text-xs mt-1">Did you follow ALL the specified requirements and constraints?</p>
                </div>
                <div>
                  <span className="font-semibold text-blue-600">2. Scenario Relevance (25 points)</span>
                  <p className="ml-4 text-xs mt-1">How well does your prompt fit the given scenario?</p>
                </div>
                <div>
                  <span className="font-semibold text-purple-600">3. Objective Alignment (25 points)</span>
                  <p className="ml-4 text-xs mt-1">Will your prompt achieve the stated objective?</p>
                </div>
                <div>
                  <span className="font-semibold text-orange-600">4. Technique Application (25 points)</span>
                  <p className="ml-4 text-xs mt-1">Proper use of prompt engineering techniques (e.g., role-play, examples, chain-of-thought)</p>
                </div>
              </div>
              <p className={`mt-3 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <span className="font-semibold">Passing Score:</span> 70/100 points
              </p>
            </div>
          </details>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Scenario</h3>
              <p className={`${isDarkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-700 bg-gray-50'} p-3 rounded`}>{currentChallenge.scenario}</p>
            </div>

            <div>
              <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Objective</h3>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{currentChallenge.objective}</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Constraints</h3>
                <ul className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                  {currentChallenge.constraints.map((constraint, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                      {constraint}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Hints</h3>
                <ul className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                  {currentChallenge.hints.map((hint, idx) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                      {hint}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Testing Area */}
      {currentChallenge && viewMode === 'active' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>Your Prompt</h3>
          <textarea
            value={userPrompt}
            onChange={(e) => {
              setUserPrompt(e.target.value);
              setPreviewEvaluation(null);
            }}
            placeholder="Write your prompt here..."
            className={`w-full h-32 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              isDarkMode 
                ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' 
                : 'border-gray-300 bg-white text-gray-900'
            }`}
          />
          <div className="mt-4 flex space-x-3">
            <button
              onClick={testPrompt}
              disabled={!userPrompt.trim() || loading}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Testing...' : 'Test Prompt'}
            </button>
            <button
              onClick={submitChallenge}
              disabled={!userPrompt.trim() || loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Submit Challenge
            </button>
          </div>
        </div>
      )}

      {/* AI Response - Only show on completed */}
      {aiResponse && viewMode === 'completed' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-4`}>AI Response</h3>
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-4 rounded-lg`}>
            <pre className={`whitespace-pre-wrap text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{aiResponse}</pre>
          </div>
        </div>
      )}

      {/* Preview Evaluation */}
      {previewEvaluation && viewMode === 'active' && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 border-2 ${isDarkMode ? 'border-yellow-700' : 'border-yellow-400'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'} flex items-center gap-2`}>
              <LucideIcon name="graded-info" size={18} /> Preview Evaluation (Not Final)
            </h3>
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estimated Score: {previewEvaluation.score}/100</span>
          </div>
          
          <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
            {/* Score Breakdown */}
            {previewEvaluation.breakdown && (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Constraint Adherence:</span>
                    <span className="font-semibold text-green-600">{previewEvaluation.breakdown.constraintAdherence}/25 points</span>
                  </div>
                  {previewEvaluation.breakdown.constraintReasoning && (
                    <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{previewEvaluation.breakdown.constraintReasoning}</p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Scenario Relevance:</span>
                    <span className="font-semibold text-blue-600">{previewEvaluation.breakdown.scenarioRelevance}/25 points</span>
                  </div>
                  {previewEvaluation.breakdown.scenarioReasoning && (
                    <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{previewEvaluation.breakdown.scenarioReasoning}</p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Objective Alignment:</span>
                    <span className="font-semibold text-purple-600">{previewEvaluation.breakdown.objectiveAlignment}/25 points</span>
                  </div>
                  {previewEvaluation.breakdown.objectiveReasoning && (
                    <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{previewEvaluation.breakdown.objectiveReasoning}</p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Technique Application:</span>
                    <span className="font-semibold text-orange-600">{previewEvaluation.breakdown.techniqueApplication}/25 points</span>
                  </div>
                  {previewEvaluation.breakdown.techniqueReasoning && (
                    <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{previewEvaluation.breakdown.techniqueReasoning}</p>
                  )}
                </div>
              </div>
            )}
            
            <div className={`mt-4 p-3 rounded ${isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{previewEvaluation.feedback}</p>
              {previewEvaluation.suggestions && previewEvaluation.suggestions.length > 0 && (
                <div>
                  <h4 className={`font-medium text-sm inline-flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}><LucideIcon name="tip" size={14} /> Suggestions:</h4>
                  <ul className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                    {previewEvaluation.suggestions.map((suggestion: string, idx: number) => (
                      <li key={idx} className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-2 mt-1.5"></span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className={`mt-3 p-2 rounded text-center text-sm inline-flex items-center justify-center gap-1.5 w-full ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              <LucideIcon name="tip" size={14} /> Tip: This analyzes your prompt quality. Submit to see actual AI response.
            </div>
          </div>
        </div>
      )}

      {/* Evaluation Results */}
      {evaluation && viewMode === 'completed' && currentChallenge && (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Challenge Complete!</h3>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}>
                {getTopicIcon(currentChallenge.topic)} {TOPICS.find(t => t.id === currentChallenge.topic)?.name}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm ${getDifficultyColor(currentChallenge.difficulty)}`}>
                {currentChallenge.difficulty}
              </span>
            </div>
          </div>
          <div className={`p-4 rounded-lg mb-4 ${evaluation.passed ? (isDarkMode ? 'bg-green-900/20' : 'bg-green-50') : (isDarkMode ? 'bg-red-900/20' : 'bg-red-50')}`}>
            <div className="flex items-center mb-2">
              <span className={`text-lg inline-flex items-center gap-1.5 ${evaluation.passed ? 'text-green-600' : 'text-red-600'}`}>
                {evaluation.passed ? <LucideIcon name="status-pass" size={18} /> : <LucideIcon name="status-fail" size={18} />}
                {evaluation.passed ? 'Challenge Passed!' : 'Challenge Failed'}
              </span>
              <span className={`ml-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Score: {evaluation.score}/100</span>
            </div>
            
            {/* Score Breakdown */}
            {evaluation.breakdown && (
              <div className={`mb-3 p-3 rounded ${isDarkMode ? 'bg-gray-700/50' : 'bg-white/50'}`}>
                <p className={`text-sm font-semibold mb-3 inline-flex items-center gap-1.5 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}><LucideIcon name="graded-info" size={14} /> Score Breakdown:</p>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Constraint Adherence:</span>
                      <span className="font-semibold text-green-600">{evaluation.breakdown.constraintAdherence}/25 points</span>
                    </div>
                    {evaluation.breakdown.constraintReasoning && (
                      <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{evaluation.breakdown.constraintReasoning}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Scenario Relevance:</span>
                      <span className="font-semibold text-blue-600">{evaluation.breakdown.scenarioRelevance}/25 points</span>
                    </div>
                    {evaluation.breakdown.scenarioReasoning && (
                      <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{evaluation.breakdown.scenarioReasoning}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Objective Alignment:</span>
                      <span className="font-semibold text-purple-600">{evaluation.breakdown.objectiveAlignment}/25 points</span>
                    </div>
                    {evaluation.breakdown.objectiveReasoning && (
                      <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{evaluation.breakdown.objectiveReasoning}</p>
                    )}
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>• Technique Application:</span>
                      <span className="font-semibold text-orange-600">{evaluation.breakdown.techniqueApplication}/25 points</span>
                    </div>
                    {evaluation.breakdown.techniqueReasoning && (
                      <p className={`ml-4 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>{evaluation.breakdown.techniqueReasoning}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>{evaluation.feedback}</p>
            {evaluation.suggestions && (
              <div>
                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>Suggestions for Improvement:</h4>
                <ul className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
                  {evaluation.suggestions.map((suggestion: string, idx: number) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={nextChallenge}
              disabled={loadingNextChallenge || !evaluation.passed}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingNextChallenge ? 'Loading Challenge...' : 'Next Challenge'}
            </button>
            <button
              onClick={tryAgain}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Try Again
            </button>
            <button
              onClick={backToSelection}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Back to Selection
            </button>
          </div>
        </div>
      )}

      {/* Original Evaluation Results (hidden) */}
      {false && evaluation && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Evaluation Results</h3>
          <div className={`p-4 rounded-lg ${evaluation.passed ? 'bg-green-50' : 'bg-red-50'}`}>
            <div className="flex items-center mb-2">
              <span className={`text-lg inline-flex items-center gap-1.5 ${evaluation.passed ? 'text-green-600' : 'text-red-600'}`}>
                {evaluation.passed ? <LucideIcon name="status-pass" size={18} /> : <LucideIcon name="status-fail" size={18} />}
                {evaluation.passed ? 'Challenge Passed!' : 'Challenge Failed'}
              </span>
              <span className="ml-4 text-sm text-gray-600">Score: {evaluation.score}/100</span>
            </div>
            <p className="text-gray-700 mb-3">{evaluation.feedback}</p>
            {evaluation.suggestions && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Suggestions for Improvement:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {evaluation.suggestions.map((suggestion: string, idx: number) => (
                    <li key={idx} className="flex items-center">
                      <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Challenges;